/**
 * App icon wrapper.
 *
 * Thin shim over `@phosphor-icons/react` so every icon call site shares
 * one set of defaults (size, weight, decorative-by-default) and a single
 * import surface. Swapping the icon library later, or changing the house
 * default size/weight, touches only this file instead of every consumer.
 *
 * Icons are decorative by default (`aria-hidden`), because in this app the
 * adjacent text label is the accessible name (nav items, buttons, chips).
 * For a standalone meaningful icon, pass `aria-hidden={false}` together
 * with an `aria-label` (or a Phosphor `<title>` child).
 */
import type { Icon as PhosphorIcon, IconProps } from '@phosphor-icons/react';

export interface AppIconProps extends IconProps {
  /** The Phosphor icon component to render, e.g. `House`. */
  icon: PhosphorIcon;
}

/** Default glyph size in px. Matches the 18px inline SVGs this replaces. */
const DEFAULT_SIZE = 18;

export function Icon({
  icon: Glyph,
  size = DEFAULT_SIZE,
  weight = 'regular',
  ...rest
}: AppIconProps) {
  return <Glyph size={size} weight={weight} aria-hidden {...rest} />;
}
